import { useRef, useState } from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Quote, MapPin, BadgeCheck } from "lucide-react";
import "./TrustSection.scss";

// `featured` (optional): draws a card on the dark gradient (.syd-trust-card--featured) — none set for now.
const testimonials = [
  { key: "abdellaoui",   name: "Pharmacie Abdellaoui", initials: "PA", color: "#2563eb" },
  { key: "benteyeb",     name: "Pharmacie Ben Teyeb",  initials: "PB", color: "#7c3aed" },
  { key: "anasibnmalek", name: "Pharmacie Benjouda",   initials: "PB", color: "#059669" },
];

const Stars = () => (
  <div className="syd-trust__stars" aria-label="5/5">
    {[...Array(5)].map((_, i) => (
      <svg key={i} width="16" height="16" viewBox="0 0 24 24" fill="#f59e0b" aria-hidden="true">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
      </svg>
    ))}
  </div>
);

const TrustSection = () => {
  const { t } = useTranslation();
  // Mobile slider (see .syd-trust__grid under 576px): the card nearest the strip's center drives the dots
  const gridRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);

  const onGridScroll = () => {
    const el = gridRef.current;
    if (!el) return;
    const center = el.getBoundingClientRect().left + el.clientWidth / 2;
    let best = 0;
    let bestDist = Infinity;
    Array.from(el.children).forEach((card, i) => {
      const r = card.getBoundingClientRect();
      const dist = Math.abs(r.left + r.width / 2 - center);
      if (dist < bestDist) { bestDist = dist; best = i; }
    });
    setActiveIdx(best);
  };

  const goTo = (i) => {
    const el = gridRef.current;
    const card = el?.children[i];
    if (!card) return;
    // scrolls only the strip (scrollIntoView would also move the page vertically)
    const elRect = el.getBoundingClientRect();
    const r = card.getBoundingClientRect();
    el.scrollBy({ left: r.left + r.width / 2 - (elRect.left + elRect.width / 2), behavior: "smooth" });
  };

  return (
    <section className="syd-trust" id="trust">
      <div className="syd-trust__bg" aria-hidden="true">
        <div className="syd-trust__bg-orb syd-trust__bg-orb--1" />
        <div className="syd-trust__bg-orb syd-trust__bg-orb--2" />
      </div>

      <div className="container">
        <motion.div
          className="syd-trust__header"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
        >
          <div className="syd-trust__label">{t("landing.nav.testimonials")}</div>
          <h2 className="syd-trust__title">{t("landing.trust.title")}</h2>
          <span className="syd-trust__underline" aria-hidden="true" />
          <p className="syd-trust__sub">
            {t("landing.trust.sub")}
          </p>
        </motion.div>

        <div className="syd-trust__grid" ref={gridRef} onScroll={onGridScroll}>
          {testimonials.map((item, i) => (
            <motion.figure
              className={`syd-trust-card${item.featured ? " syd-trust-card--featured" : ""}`}
              key={item.key}
              style={{ "--accent": item.color }}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1, duration: 0.5 }}
              viewport={{ once: true }}
            >
              <div className="syd-trust-card__top">
                <Stars />
                <Quote className="syd-trust-card__quote-icon" size={40} strokeWidth={1.5} aria-hidden="true" />
              </div>
              <blockquote className="syd-trust-card__quote">{t(`landing.trust.${item.key}_text`)}</blockquote>
              <figcaption className="syd-trust-card__author">
                <div className="syd-trust-card__avatar">
                  {item.initials}
                  <BadgeCheck className="syd-trust-card__verified" size={16} aria-hidden="true" />
                </div>
                <div>
                  <div className="syd-trust-card__name">{item.name}</div>
                  <div className="syd-trust-card__role">
                    <MapPin size={12} aria-hidden="true" />
                    {t(`landing.trust.${item.key}_role`)}
                  </div>
                </div>
              </figcaption>
            </motion.figure>
          ))}
        </div>

        <div className="syd-trust__dots">
          {testimonials.map((item, i) => (
            <button
              key={item.key}
              type="button"
              className={i === activeIdx ? "is-active" : ""}
              onClick={() => goTo(i)}
              aria-label={item.name}
              aria-current={i === activeIdx}
            />
          ))}
        </div>
      </div>
    </section>
  );
};

export default TrustSection;
